import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Upload, Crown, TrendingUp, Globe, Users, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Footer from '@/components/Footer';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isPremium, setIsPremium] = useState(false);
  const [hasAnalysis, setHasAnalysis] = useState(false);

  useEffect(() => {
    const premium = localStorage.getItem('isPremium') === 'true';
    const image = localStorage.getItem('uploadedImage');
    setIsPremium(premium);
    setHasAnalysis(!!image);
  }, []);

  const stats = [
    { label: "Overall Score", value: "7.8/10", icon: TrendingUp, color: "text-purple-600" },
    { label: "Global Rank", value: "Top 23%", icon: Globe, color: "text-blue-600" },
    { label: "Best Region", value: "Los Angeles", icon: Users, color: "text-pink-600" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-purple-600" />
            <h1 className="text-2xl font-bold text-gradient">AttractivenessAI</h1>
          </div>
          <div className="flex items-center gap-4">
            {isPremium ? (
              <div className="flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 px-4 py-2 rounded-full">
                <Crown className="h-5 w-5 text-purple-600" />
                <span className="font-semibold text-purple-600">Premium Member</span>
              </div>
            ) : (
              <Button 
                className="gradient-primary text-white"
                onClick={() => navigate('/results')}
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Premium
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto"
        >
          {/* Welcome Section */}
          <div className="mb-12">
            <h2 className="text-4xl font-bold mb-4">Welcome Back!</h2>
            <p className="text-xl text-gray-600">
              {hasAnalysis 
                ? "Here's your attractiveness analysis dashboard" 
                : "Upload a photo to get started with your analysis"}
            </p>
          </div>

          {hasAnalysis ? (
            <>
              {/* Stats Grid */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="p-6 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow">
                      <stat.icon className={`h-8 w-8 ${stat.color} mb-3`} />
                      <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                <Card className="p-8 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow">
                  <Upload className="h-12 w-12 text-purple-600 mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Analyze New Photo</h3>
                  <p className="text-gray-600 mb-4">
                    Upload another photo to compare results and track your progress
                  </p>
                  <Button 
                    className="gradient-primary text-white"
                    onClick={() => navigate('/')}
                  >
                    Upload New Photo
                  </Button>
                </Card>

                <Card className="p-8 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-shadow">
                  <History className="h-12 w-12 text-pink-600 mb-4" />
                  <h3 className="text-2xl font-bold mb-2">View Full Results</h3>
                  <p className="text-gray-600 mb-4">
                    See your complete analysis with regional and demographic insights
                  </p>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/results')}
                  >
                    View Results
                  </Button>
                </Card>
              </div>

              {/* Premium Upsell for Free Users */}
              {!isPremium && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Card className="p-8 gradient-primary text-white relative overflow-hidden">
                    <div className="relative z-10">
                      <Crown className="h-12 w-12 mb-4" />
                      <h3 className="text-3xl font-bold mb-4">
                        Unlock Premium Features
                      </h3>
                      <p className="text-lg mb-6 opacity-90">
                        Get complete access to all regions, demographics, and personalized recommendations
                      </p>
                      <Button 
                        size="lg"
                        className="bg-white text-purple-600 hover:bg-gray-100"
                        onClick={() => navigate('/results')}
                      >
                        <Crown className="h-5 w-5 mr-2" />
                        Upgrade Now - Limited Time Offer
                      </Button>
                    </div>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />
                  </Card>
                </motion.div>
              )}
            </>
          ) : (
            /* No Analysis Yet */
            <Card className="p-12 text-center bg-white/80 backdrop-blur-sm">
              <Upload className="h-20 w-20 text-purple-600 mx-auto mb-6" />
              <h3 className="text-3xl font-bold mb-4">Get Started</h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Upload your first photo to discover your attractiveness ratings across 
                different regions and demographics worldwide
              </p>
              <Button 
                size="lg"
                className="gradient-primary text-white"
                onClick={() => navigate('/')}
              >
                <Upload className="h-5 w-5 mr-2" />
                Upload Your First Photo
              </Button>
            </Card>
          )}
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Dashboard;